import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Check, ChevronsUpDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { useTripSearch } from "@/hooks/useInvoices";
import NewTripFields, {
  EMPTY_NEW_TRIP,
  type NewTripData,
} from "@/components/invoicing/NewTripFields";
import type { Trip } from "@shared/schema";

export type { NewTripData } from "@/components/invoicing/NewTripFields";

export type TripMode = "new" | "existing" | null;

export interface TripSectionValue {
  mode: TripMode;
  existingTripId: number | null;
  existingTrip: Trip | null;
  newTrip: NewTripData;
}

interface TripSectionProps {
  value: TripSectionValue;
  onChange: (value: TripSectionValue) => void;
}

export const DEFAULT_TRIP_SECTION: TripSectionValue = {
  mode: null,
  existingTripId: null,
  existingTrip: null,
  newTrip: EMPTY_NEW_TRIP,
};

export default function TripSection({ value, onChange }: TripSectionProps) {
  const [tripSearch, setTripSearch] = useState("");
  const [tripPopoverOpen, setTripPopoverOpen] = useState(false);

  const { data: filteredTrips = [] } = useTripSearch(tripSearch);

  const setMode = (mode: TripMode) => {
    onChange({
      ...value,
      mode,
      existingTripId: mode === "existing" ? value.existingTripId : null,
      existingTrip: mode === "existing" ? value.existingTrip : null,
    });
  };

  const selectTrip = (trip: Trip) => {
    onChange({
      ...value,
      mode: "existing",
      existingTripId: trip.id,
      existingTrip: trip,
    });
    setTripPopoverOpen(false);
    setTripSearch("");
  };

  const updateNewTrip = (patch: Partial<NewTripData>) => {
    onChange({
      ...value,
      newTrip: { ...value.newTrip, ...patch },
    });
  };

  return (
    <Card>
      <CardHeader className="pb-4">
        <CardTitle className="text-base">Trip Details</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Mode Toggle — AR-210 (TC-08.2): the selected mode was shown by colour only. */}
        <div className="grid grid-cols-2 gap-2" role="group" aria-label="Trip">
          <Button
            type="button"
            variant={value.mode === "new" ? "default" : "outline"}
            aria-pressed={value.mode === "new"}
            onClick={() => setMode("new")}
            className="w-full"
          >
            Create a New Trip
          </Button>
          <Button
            type="button"
            variant={value.mode === "existing" ? "default" : "outline"}
            aria-pressed={value.mode === "existing"}
            onClick={() => setMode("existing")}
            className="w-full"
          >
            Select Existing Trip
          </Button>
        </div>

        {/* Select Existing Trip */}
        {value.mode === "existing" && (
          <div className="space-y-3">
            <Popover open={tripPopoverOpen} onOpenChange={setTripPopoverOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  className={cn(
                    "w-full justify-between font-normal",
                    !value.existingTrip && "text-muted-foreground"
                  )}
                >
                  {value.existingTrip
                    ? value.existingTrip.name
                    : "Search trips..."}
                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              {/* AR-316: 400px overflows a 375px viewport */}
              <PopoverContent
                className="w-[min(400px,calc(100vw-2rem))] p-0"
                align="start"
                collisionPadding={8}
              >
                <Command shouldFilter={false} label="Search trips">
                  <CommandInput
                    placeholder="Search by name or trip #..."
                    value={tripSearch}
                    onValueChange={setTripSearch}
                  />
                  <CommandList>
                    <CommandEmpty>No trips found.</CommandEmpty>
                    <CommandGroup>
                      <ScrollArea className="h-[200px]">
                        {filteredTrips.map((trip) => (
                          <CommandItem
                            key={trip.id}
                            value={String(trip.id)}
                            onSelect={() => selectTrip(trip)}
                          >
                            <div className="flex-1">
                              <div className="font-medium">{trip.name}</div>
                              <div className="text-xs text-muted-foreground flex gap-2">
                                {trip.recordLocator && (
                                  <span>ID: {trip.recordLocator}</span>
                                )}
                              </div>
                            </div>
                            {value.existingTripId === trip.id && (
                              <Check className="h-4 w-4 text-primary" />
                            )}
                          </CommandItem>
                        ))}
                      </ScrollArea>
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>

            {value.existingTrip && (
              <div className="rounded-md border p-3 bg-muted/30 space-y-1">
                <div className="flex items-center justify-between">
                  <span className="font-medium">{value.existingTrip.name}</span>
                  {value.existingTrip.recordLocator && (
                    <Badge variant="secondary">
                      Trip ID: {value.existingTrip.recordLocator}
                    </Badge>
                  )}
                </div>
                {value.existingTrip.isGroup && value.existingTrip.groupName && (
                  <div className="text-sm text-muted-foreground">
                    Group Booking Number: {value.existingTrip.groupName}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {value.mode === "new" && (
          <NewTripFields
            value={value.newTrip}
            onChange={updateNewTrip}
            idPrefix="trip-section"
          />
        )}
      </CardContent>
    </Card>
  );
}
